// env.js
'use strict';
import { getIsInBrowserMainThread } from '../resource/getJsEnvironment.js';
import { castConfigValue } from '../resource/castConfigValue.js';

const isBrowserMainThread = getIsInBrowserMainThread();

const castEnv = () => {
  Object.keys(env).forEach(key => {
    env[key] = castConfigValue({ dataKey: key, value: env[key] });
  });
};

// Build-time release identifiers come from globalThis.__BUILD_ENV__
// (set by ../setBuildEnv.js which is imported only by the main app
// entry). Avoiding `process.env.TAG_NAME / BRANCH_NAME / SHORT_SHA`
// references here keeps the Service Worker bundle free of per-build
// inlined values, so SW chunkhash stays stable across Docker builds.
const buildEnv = globalThis.__BUILD_ENV__ || {};
// If this fires, the import order broke (env.js evaluated before
// setBuildEnv.js) and TAG_NAME / BRANCH_NAME / SHORT_SHA are undefined.
if (isBrowserMainThread && !globalThis.__BUILD_ENV__) {
  // eslint-disable-next-line no-console
  console.error(
    '[env] globalThis.__BUILD_ENV__ is not set — setBuildEnv.js was not loaded before env.js'
  );
}

const env = Object.assign(
  {},
  process.env.DOTENV_PARSED_ENV,
  process.env,
  isBrowserMainThread ? window.__SSR_ENVIRONMENT__ : undefined,
  {
    BRANCH_NAME: buildEnv.BRANCH_NAME,
    TAG_NAME: buildEnv.TAG_NAME,
    SHORT_SHA: buildEnv.SHORT_SHA,
  }
);

castEnv();

// replace values with SSR remote config
if (isBrowserMainThread) {
  Object.keys(env).forEach(key => {
    const rcValue =
      window.WebApp?.remoteConfig?.[key] ??
      window.__PRELOADED_STATE__?.remoteConfig?.[key];
    if (undefined !== rcValue) {
      env[key] = castConfigValue({ dataKey: key, value: rcValue });
    }
  });

  // keys should never been remote configured
  env.BRANCH_NAME = buildEnv.BRANCH_NAME;
  env.TAG_NAME = buildEnv.TAG_NAME;
  env.SHORT_SHA = buildEnv.SHORT_SHA;
}

let priorityConfig = {};

/**
 * Update env with new config values.
 * After applying newConfig, priorityConfig values are always re-applied on top
 * to ensure high-priority sources (e.g. GrowthBook) are never overwritten
 * by lower-priority sources (e.g. remote config from API).
 *
 * Each value is normalized via castConfigValue so callers always read typed values.
 *
 * @param {object} newConfig - config values to write into env.
 * @param {boolean} [isPriorityConfig] - if true, store newConfig as priorityConfig
 *   so its values persist across future updateEnv calls.
 */
export const updateEnv = (newConfig, isPriorityConfig) => {
  try {
    if (isPriorityConfig) {
      priorityConfig = newConfig;
    }
    Object.keys(newConfig).forEach(key => {
      env[key] = castConfigValue({ dataKey: key, value: newConfig[key] });
    });
    Object.keys(priorityConfig).forEach(key => {
      env[key] = castConfigValue({ dataKey: key, value: priorityConfig[key] });
    });
    // eslint-disable-next-line no-empty
  } catch (_) {}
};

export default env;
